import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { format } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usersAPI, defaultAssignmentsAPI } from '../../services/api';
import type { RideHistoryWeekStatus } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { User, UserRole } from '../../types';

type SortColumn = 'email' | 'name' | 'surname' | 'role' | 'plan' | 'status' | 'bookings' | 'ledRides' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const UsersTab: React.FC<{ onNavigateToEvent?: (seriesId: string) => void }> = ({ onNavigateToEvent }) => {
  const { t } = useTranslation();
  const { user: currentUser, isPowerAdmin, isTrainer, isAdmin } = useAuth();
  const toast = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [assignmentsUser, setAssignmentsUser] = useState<User | null>(null);
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [openKebab, setOpenKebab] = useState<string | null>(null);
  const [kebabPos, setKebabPos] = useState({ top: 0, left: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [sortColumn, setSortColumn] = useState<SortColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const queryClient = useQueryClient();

  useEscapeKey(() => {
    if (profileUser) setProfileUser(null);
    else if (assignmentsUser) setAssignmentsUser(null);
    else if (editingUser) setEditingUser(null);
    else if (deletingUser) setDeletingUser(null);
  });

  const { data: userAssignments = [], isLoading: isAssignmentsLoading } = useQuery({
    queryKey: ['userAssignments', assignmentsUser?.id],
    queryFn: () => defaultAssignmentsAPI.getUserDefaultAssignments(assignmentsUser!.id),
    enabled: !!assignmentsUser,
  });

  const { data: rideHistory, isLoading: isHistoryLoading } = useQuery({
    queryKey: ['rideHistory', profileUser?.id],
    queryFn: () => usersAPI.getRideHistory(profileUser!.id),
    enabled: !!profileUser && isAdmin,
  });

  const grantAttendanceMutation = useMutation({
    mutationFn: ({ userId, bookingId }: { userId: string; bookingId: string }) =>
      usersAPI.grantAttendance(userId, bookingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rideHistory', profileUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success(t('admin.attendanceGranted'));
    },
    onError: (error: any) => {
      toast.error(t('admin.errorGrantingAttendance', { error: error.response?.data?.error || error.message }));
    },
  });

  const openProfile = (user: User) => {
    if (!isAdmin) return;
    setShowHistory(false);
    setProfileUser(user);
  };

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  // Filter users based on search query and role
  const filteredUsers = useMemo(() => {
    return users.filter((user: User) => {
      // Role filter
      if (roleFilter !== 'ALL' && user.role !== roleFilter) return false;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.trim().toLowerCase();
        const email = (user.email || '').toLowerCase();
        const name = (user.name || '').toLowerCase();
        const surname = (user.surname || '').toLowerCase();
        const fullName = `${name} ${surname}`.trim().toLowerCase();

        if (!(email.includes(query) || name.includes(query) || surname.includes(query) || fullName.includes(query))) {
          return false;
        }
      }

      return true;
    });
  }, [users, searchQuery, roleFilter]);

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];
    sorted.sort((a: any, b: any) => {
      let aVal: any;
      let bVal: any;

      switch (sortColumn) {
        case 'email':
          aVal = (a.email || '').toLowerCase();
          bVal = (b.email || '').toLowerCase();
          break;
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'surname':
          aVal = (a.surname || '').toLowerCase();
          bVal = (b.surname || '').toLowerCase();
          break;
        case 'role':
          aVal = a.role || '';
          bVal = b.role || '';
          break;
        case 'plan':
          aVal = a.membershipSelection?.currentWeeklyPlan || '';
          bVal = b.membershipSelection?.currentWeeklyPlan || '';
          break;
        case 'status':
          aVal = a.membershipSelection?.status || '';
          bVal = b.membershipSelection?.status || '';
          break;
        case 'bookings':
          aVal = a._count?.bookings || 0;
          bVal = b._count?.bookings || 0;
          break;
        case 'ledRides':
          aVal = a._count?.ledSlots || 0;
          bVal = b._count?.ledSlots || 0;
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const SortIcon: React.FC<{ column: SortColumn }> = ({ column }) => {
    if (sortColumn !== column) {
      return <span className="ml-1 text-gray-300 inline-block w-3">&#8597;</span>;
    }
    return (
      <span className="ml-1 text-primary-500 inline-block w-3">
        {sortDirection === 'asc' ? '\u25B2' : '\u25BC'}
      </span>
    );
  };

  const createUserMutation = useMutation({
    mutationFn: usersAPI.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateForm(false);
      toast.success(t('admin.userCreatedSuccess'));
    },
    onError: (error: any) => {
      toast.error(t('admin.errorCreatingUser', { error: error.response?.data?.error || error.message }));
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => usersAPI.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
      toast.success(t('admin.userUpdatedSuccess'));
    },
    onError: (error: any) => {
      toast.error(t('admin.errorUpdatingUser', { error: error.response?.data?.error || error.message }));
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: usersAPI.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeletingUser(null);
      toast.success(t('admin.userDeletedSuccess'));
    },
    onError: (error: any) => {
      toast.error(t('admin.errorDeletingUser', { error: error.response?.data?.error || error.message }));
    },
  });

  const handleCreateUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      email: formData.get('email') as string,
      password: formData.get('password') as string,
      name: formData.get('name') as string || undefined,
      surname: formData.get('surname') as string || undefined,
      role: (formData.get('role') as UserRole) || 'USER',
    };
    createUserMutation.mutate(data);
  };

  const handleEditUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingUser) return;

    const formData = new FormData(e.currentTarget);
    const data: { email?: string; password?: string; name?: string; surname?: string; role?: UserRole } = {};

    const newEmail = formData.get('email') as string;
    if (newEmail && newEmail !== editingUser.email) {
      data.email = newEmail;
    }

    const newName = formData.get('name') as string;
    const currentName = editingUser.name || '';
    if (newName !== currentName) {
      data.name = newName || undefined;
    }

    const newSurname = formData.get('surname') as string;
    const currentSurname = editingUser.surname || '';
    if (newSurname !== currentSurname) {
      data.surname = newSurname || undefined;
    }

    const newPassword = formData.get('password') as string;
    if (newPassword && newPassword.trim().length > 0) {
      data.password = newPassword;
    }

    const newRole = formData.get('role') as UserRole;
    if (newRole && newRole !== editingUser.role) {
      data.role = newRole;
    }

    // Only update if there are changes
    if (Object.keys(data).length > 0) {
      updateUserMutation.mutate({ id: editingUser.id, data });
    } else {
      setEditingUser(null);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeletingUser(user);
  };

  const handleDeleteConfirm = () => {
    if (deletingUser) {
      deleteUserMutation.mutate(deletingUser.id);
    }
  };

  const canDeleteUser = (user: User): boolean => {
    // Admin cannot delete themselves
    if (user.id === currentUser?.id) {
      return false;
    }
    // Only POWER_ADMIN can delete POWER_ADMIN users
    if (user.role === 'POWER_ADMIN' && !isPowerAdmin) {
      return false;
    }
    return true;
  };

  const canEditUser = (user: User): boolean => {
    // Only POWER_ADMIN can edit POWER_ADMIN users
    if (user.role === 'POWER_ADMIN' && !isPowerAdmin) {
      return false;
    }
    return true;
  };

  const getDeleteWarning = (user: User): string => {
    if (user.id === currentUser?.id) {
      return t('admin.cannotDeleteSelf');
    }
    if (user.role === 'POWER_ADMIN') {
      return t('admin.deletePowerAdminWarning');
    }
    if (user.role === 'ADMIN') {
      const adminCount = users.filter((u) => u.role === 'ADMIN' || u.role === 'POWER_ADMIN').length;
      if (adminCount <= 1) {
        return t('admin.cannotDeleteLastAdmin');
      }
      return t('admin.deleteAdminWarning');
    }
    return t('admin.deleteUserConfirm');
  };

  // Close kebab menu on click outside
  useEffect(() => {
    if (!openKebab) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-kebab]')) {
        setOpenKebab(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openKebab]);

  const KebabMenu: React.FC<{ user: User }> = ({ user }) => {
    const isOpen = openKebab === user.id;

    const handleToggle = (e: React.MouseEvent) => {
      if (isOpen) {
        setOpenKebab(null);
      } else {
        const rect = e.currentTarget.getBoundingClientRect();
        setKebabPos({ top: rect.bottom + 4, left: rect.right - 192 });
        setOpenKebab(user.id);
      }
    };

    return (
      <>
        <button
          type="button"
          data-kebab
          onClick={handleToggle}
          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
          title={t('admin.actions')}
        >
          <span className="flex flex-col gap-[3px]">
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
            <span className="w-1 h-1 bg-gray-500 rounded-full" />
          </span>
        </button>
        {isOpen && ReactDOM.createPortal(
          <div
            data-kebab
            className="fixed z-[9999] bg-white border rounded-lg shadow-lg py-1 w-48"
            style={{ top: kebabPos.top, left: kebabPos.left }}
          >
            <button
              type="button"
              onClick={() => { setAssignmentsUser(user); setOpenKebab(null); }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              {t('admin.defaultAssignments')}
            </button>
            {canEditUser(user) ? (
              <button
                type="button"
                onClick={() => { setEditingUser(user); setOpenKebab(null); }}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                {t('admin.edit')}
              </button>
            ) : (
              <span className="block px-4 py-2 text-sm text-gray-400">{t('admin.edit')}</span>
            )}
            {canDeleteUser(user) ? (
              <button
                type="button"
                onClick={() => { handleDeleteClick(user); setOpenKebab(null); }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              >
                {t('admin.delete')}
              </button>
            ) : (
              <span className="block px-4 py-2 text-sm text-gray-400">{t('admin.delete')}</span>
            )}
          </div>,
          document.body
        )}
      </>
    );
  };

  const dayOrder: Record<string, number> = { MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6, SUNDAY: 7 };

  const getDayFromDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    return ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][d.getDay()];
  };

  const StatusBadge: React.FC<{ status: RideHistoryWeekStatus }> = ({ status }) => {
    const config: Record<RideHistoryWeekStatus, { className: string; label: string }> = {
      ATTENDED: { className: 'bg-green-100 text-green-800', label: t('admin.statusAttended') },
      NO_SHOW: { className: 'bg-amber-100 text-amber-800', label: t('admin.statusNoShow') },
      NOT_BOOKED: { className: 'bg-gray-100 text-gray-700', label: t('admin.statusNotBooked') },
      CANCELLED: { className: 'bg-gray-100 text-gray-500 line-through', label: t('admin.statusCancelled') },
      NO_RIDE: { className: 'bg-transparent text-gray-400', label: t('admin.statusNoRide') },
    };
    const { className, label } = config[status];
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${className}`}>
        {label}
      </span>
    );
  };

  return (
    <div>
      {/* User Profile Modal (admin only) */}
      {isAdmin && profileUser && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setProfileUser(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {t('admin.userProfile')}
              </h3>
              <button
                onClick={() => setProfileUser(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
                title={t('admin.close')}
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              <div className="mb-4">
                <p className="text-lg font-medium text-gray-900">
                  {[profileUser.name, profileUser.surname].filter(Boolean).join(' ') || profileUser.email}
                </p>
                {profileUser.email && (
                  <p className="text-sm text-gray-500">{profileUser.email}</p>
                )}
              </div>

              {isHistoryLoading || !rideHistory ? (
                <p className="text-sm text-gray-500">{t('admin.loading')}</p>
              ) : (
                <>
                  {/* Headline stats */}
                  <div className="grid grid-cols-3 gap-3 mb-2">
                    <button
                      type="button"
                      onClick={() => setShowHistory((prev) => !prev)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border transition-colors ${
                        showHistory ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      title={t('admin.clickRidesForHistory')}
                    >
                      <span className="text-2xl font-bold text-primary-600">{rideHistory.user.rideCount}</span>
                      <span className="text-xs text-gray-500">{t('admin.rides')}</span>
                    </button>
                    <div className="flex flex-col items-center justify-center p-3 rounded-lg border border-gray-200">
                      <span className="text-2xl font-bold text-gray-800">{rideHistory.user.currentStreak}</span>
                      <span className="text-xs text-gray-500">{t('admin.streak')}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-3 rounded-lg border border-gray-200">
                      <span className="text-2xl font-bold text-gray-800">{rideHistory.user.totalPoints}</span>
                      <span className="text-xs text-gray-500">{t('admin.points')}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 text-center mb-4">{t('admin.clickRidesForHistory')}</p>

                  {/* Ride history */}
                  {showHistory && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700">{t('admin.last10Weeks')}</h4>
                      {rideHistory.weeks.map((week) => (
                        <div
                          key={week.weekStart}
                          className="p-3 border border-gray-200 rounded-lg"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {t('admin.weekOf', { date: format(new Date(week.weekStart), 'd MMM yyyy') })}
                            </span>
                            <div className="flex items-center gap-2">
                              <StatusBadge status={week.status} />
                              {isAdmin && week.status === 'NO_SHOW' && week.grantBookingId && (
                                <button
                                  type="button"
                                  onClick={() => grantAttendanceMutation.mutate({
                                    userId: profileUser.id,
                                    bookingId: week.grantBookingId as string,
                                  })}
                                  disabled={grantAttendanceMutation.isPending}
                                  className="bg-primary-600 text-white px-2 py-1 rounded text-xs hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {t('admin.grantAttendance')}
                                </button>
                              )}
                            </div>
                          </div>
                          {week.events.length > 0 && (
                            <div className="mt-2 space-y-0.5">
                              {week.events.map((ev) => (
                                <p
                                  key={ev.slotId}
                                  className={`text-xs ${ev.cancelled ? 'text-gray-400 line-through' : 'text-gray-500'}`}
                                >
                                  {ev.title} · {format(new Date(ev.date), 'd MMM')} {ev.startTime}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assignments Modal */}
      {assignmentsUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {t('admin.defaultAssignments')}: {[assignmentsUser.name, assignmentsUser.surname].filter(Boolean).join(' ') || assignmentsUser.email}
              </h3>
              <button
                onClick={() => setAssignmentsUser(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {isAssignmentsLoading ? (
                <p className="text-sm text-gray-500">{t('admin.loading')}</p>
              ) : userAssignments.length === 0 ? (
                <p className="text-sm text-gray-500">{t('admin.noDefaultAssignments')}</p>
              ) : (
                <div className="space-y-2">
                  {[...userAssignments]
                    .sort((a: any, b: any) => {
                      const dayA = dayOrder[getDayFromDate(a.trainingSlot?.date || a.series?.startDate)] || 99;
                      const dayB = dayOrder[getDayFromDate(b.trainingSlot?.date || b.series?.startDate)] || 99;
                      return dayA - dayB;
                    })
                    .map((assignment: any) => {
                      const slot = assignment.trainingSlot || assignment.series;
                      const day = getDayFromDate(slot?.date || slot?.startDate);
                      return (
                        <div
                          key={assignment.id}
                          className="flex items-center justify-between p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                          onDoubleClick={() => {
                            const seriesId = assignment.seriesId || slot?.id;
                            if (seriesId && onNavigateToEvent) {
                              setAssignmentsUser(null);
                              onNavigateToEvent(seriesId);
                            }
                          }}
                          title={t('admin.doubleClickToOpen')}
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-900">{slot?.title || t('admin.training')}</p>
                            <p className="text-xs text-gray-500">
                              {day.charAt(0) + day.slice(1).toLowerCase()} · {slot?.startTime} – {slot?.endTime}
                            </p>
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            slot?.type === 'SWIMMING' ? 'bg-blue-100 text-blue-800' :
                            slot?.type === 'BIKING' ? 'bg-yellow-100 text-yellow-800' :
                            slot?.type === 'RUNNING' ? 'bg-green-100 text-green-800' :
                            slot?.type === 'GYM' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {slot?.type}
                          </span>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">{t('admin.usersManagement')}</h2>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setEditingUser(null);
            }}
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
          >
            {showCreateForm ? t('admin.cancel') : t('admin.addUser')}
          </button>
        </div>
      </div>

      {/* Single User Creation Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-xl font-semibold mb-4">{t('admin.addUser')}</h3>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full border rounded px-3 py-2"
                  placeholder="john.doe@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.newPassword')}
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  minLength={6}
                  className="w-full border rounded px-3 py-2"
                  placeholder="••••••"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.name')}
                </label>
                <input
                  type="text"
                  name="name"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.surname')}
                </label>
                <input
                  type="text"
                  name="surname"
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.role')}
                </label>
                <select name="role" className="w-full border rounded px-3 py-2" defaultValue="USER" disabled={isTrainer}>
                  <option value="USER">{t('admin.user')}</option>
                  <option value="TRAINER">{t('admin.trainer')}</option>
                  <option value="ADMIN">{t('admin.admin_role')}</option>
                  {isPowerAdmin && <option value="POWER_ADMIN">{t('admin.powerAdmin')}</option>}
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={createUserMutation.isPending}
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createUserMutation.isPending ? t('admin.saving') : t('admin.addUser')}
            </button>
          </form>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">{t('admin.edit')}: {editingUser.email}</h3>
              <button
                onClick={() => setEditingUser(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingUser.email}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={editingUser.name || ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('admin.surname')}
                  </label>
                  <input
                    type="text"
                    name="surname"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={editingUser.surname || ''}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.newPassword')}
                </label>
                <input
                  type="password"
                  name="password"
                  className="w-full border rounded px-3 py-2"
                  placeholder="••••••"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('admin.role')}
                </label>
                <select
                  name="role"
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingUser.role}
                  disabled={isTrainer}
                >
                  <option value="USER">{t('admin.user')}</option>
                  <option value="TRAINER">{t('admin.trainer')}</option>
                  <option value="ADMIN">{t('admin.admin_role')}</option>
                  {isPowerAdmin && <option value="POWER_ADMIN">{t('admin.powerAdmin')}</option>}
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={updateUserMutation.isPending}
                  className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateUserMutation.isPending ? t('admin.saving') : t('admin.save')}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-xl font-semibold text-accent-600">{t('admin.deleteUser')}</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">{getDeleteWarning(deletingUser)}</p>
              {(deletingUser.role === 'ADMIN' || deletingUser.role === 'POWER_ADMIN') && deletingUser.id !== currentUser?.id && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
                  <strong>{t('admin.admin_role')}:</strong> {t('admin.deleteAdminPrivilegesWarning')}
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleDeleteConfirm}
                  disabled={!canDeleteUser(deletingUser) || deleteUserMutation.isPending}
                  className="bg-accent-600 text-white px-4 py-2 rounded hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteUserMutation.isPending ? t('admin.loading') : t('admin.deleteUser')}
                </button>
                <button
                  onClick={() => setDeletingUser(null)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  {t('admin.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder={t('admin.searchByNameEmail')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border rounded px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="ALL">{t('admin.allRoles')}</option>
              <option value="USER">{t('admin.user')}</option>
              <option value="TRAINER">{t('admin.trainer')}</option>
              <option value="ADMIN">{t('admin.admin_role')}</option>
              <option value="POWER_ADMIN">{t('admin.powerAdmin')}</option>
            </select>
            {(searchQuery || roleFilter !== 'ALL') && (
              <button
                onClick={() => { setSearchQuery(''); setRoleFilter('ALL'); }}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                {t('admin.reset')}
              </button>
            )}
          </div>
          {(searchQuery || roleFilter !== 'ALL') && (
            <p className="text-xs text-gray-500 mt-2">
              {t('admin.totalUsers', { count: filteredUsers.length })} / {users.length}
            </p>
          )}
        </div>

        {/* Desktop: Table */}
        <div className="hidden lg:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {([
                  ['email', t('admin.email')],
                  ['name', t('admin.name')],
                  ['surname', t('admin.surname')],
                  ['role', t('admin.role')],
                  ['plan', t('admin.plan')],
                  ['status', t('admin.status')],
                  ['bookings', t('admin.bookings')],
                  ['ledRides', t('admin.led')],
                  ['createdAt', t('admin.createdAt')],
                ] as [SortColumn, string][]).map(([col, label]) => (
                  <th
                    key={col}
                    onClick={() => handleSort(col)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none"
                  >
                    {label}
                    <SortIcon column={col} />
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('admin.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                    {t('admin.loading')}
                  </td>
                </tr>
              ) : sortedUsers.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-4 text-center text-gray-500">
                    {t('admin.noUsers')}
                  </td>
                </tr>
              ) : (
                sortedUsers.map((user: any) => (
                  <tr
                    key={user.id}
                    onClick={() => openProfile(user)}
                    className={isAdmin ? 'cursor-pointer hover:bg-gray-50' : undefined}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {user.email || '-'}
                      {user.id === currentUser?.id && (
                        <span className="ml-2 text-xs text-gray-500">({t('admin.you')})</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {user.surname || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.role === 'POWER_ADMIN'
                            ? 'bg-purple-100 text-purple-800'
                            : user.role === 'ADMIN'
                            ? 'bg-accent-100 text-accent-800'
                            : user.role === 'TRAINER'
                            ? 'bg-teal-100 text-teal-800'
                            : 'bg-primary-100 text-primary-800'
                        }`}
                      >
                        {user.role === 'POWER_ADMIN' ? t('admin.powerAdmin') : user.role === 'ADMIN' ? t('admin.admin_role') : user.role === 'TRAINER' ? t('admin.trainer') : t('admin.user')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.membershipSelection?.currentWeeklyPlan
                        ? user.membershipSelection.currentWeeklyPlan.replace('WEEKLY_', '') + 'x/week'
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {user.membershipSelection?.status ? (
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.membershipSelection.status === 'ACTIVE'
                              ? 'bg-green-100 text-green-800'
                              : user.membershipSelection.status === 'TRIALING'
                              ? 'bg-blue-100 text-blue-800'
                              : user.membershipSelection.status === 'PAST_DUE'
                              ? 'bg-yellow-100 text-yellow-800'
                              : user.membershipSelection.status === 'CANCELED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {user.membershipSelection.status}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user._count?.bookings || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user._count?.ledSlots ? (
                        <span className="flex items-center gap-1">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#FF6600" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 20h20" />
                            <path d="M4 20l2-14 4 6 2-8 2 8 4-6 2 14" />
                          </svg>
                          {user._count.ledSlots}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(user.createdAt), 'dd MMM yyyy')}
                    </td>
                    <td
                      className="px-6 py-4 whitespace-nowrap text-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <KebabMenu user={user} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile/Tablet: Cards */}
        <div className="lg:hidden divide-y divide-gray-200">
          {isLoading ? (
            <div className="px-4 py-8 text-center text-gray-500">{t('admin.loading')}</div>
          ) : sortedUsers.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              {t('admin.noUsers')}
            </div>
          ) : (
            sortedUsers.map((user: any) => (
              <div
                key={user.id}
                onClick={() => openProfile(user)}
                className={`p-4 space-y-2 ${isAdmin ? 'cursor-pointer active:bg-gray-50' : ''}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {user.email || '-'}
                      {user.id === currentUser?.id && (
                        <span className="ml-1 text-xs text-gray-500">({t('admin.you')})</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      {[user.name, user.surname].filter(Boolean).join(' ') || '-'}
                    </div>
                  </div>
                  <span
                    className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'POWER_ADMIN'
                        ? 'bg-purple-100 text-purple-800'
                        : user.role === 'ADMIN'
                        ? 'bg-accent-100 text-accent-800'
                        : user.role === 'TRAINER'
                        ? 'bg-teal-100 text-teal-800'
                        : 'bg-primary-100 text-primary-800'
                    }`}
                  >
                    {user.role === 'POWER_ADMIN' ? t('admin.powerAdmin') : user.role === 'ADMIN' ? t('admin.admin_role') : user.role === 'TRAINER' ? t('admin.trainer') : t('admin.user')}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                  {user.membershipSelection?.currentWeeklyPlan && (
                    <span>
                      {user.membershipSelection.currentWeeklyPlan.replace('WEEKLY_', '')}x/week
                    </span>
                  )}
                  {user.membershipSelection?.status && (
                    <span
                      className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        user.membershipSelection.status === 'ACTIVE'
                          ? 'bg-green-100 text-green-800'
                          : user.membershipSelection.status === 'TRIALING'
                          ? 'bg-blue-100 text-blue-800'
                          : user.membershipSelection.status === 'PAST_DUE'
                          ? 'bg-yellow-100 text-yellow-800'
                          : user.membershipSelection.status === 'CANCELED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {user.membershipSelection.status}
                    </span>
                  )}
                  <span>{user._count?.bookings || 0} {t('admin.bookings').toLowerCase()}</span>
                  {user._count?.ledSlots ? (
                    <span className="flex items-center gap-0.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#FF6600" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 20h20" />
                        <path d="M4 20l2-14 4 6 2-8 2 8 4-6 2 14" />
                      </svg>
                      {user._count.ledSlots} {t('admin.led').toLowerCase()}
                    </span>
                  ) : null}
                  <span>{format(new Date(user.createdAt), 'dd MMM yyyy')}</span>
                </div>
                <div className="flex justify-end pt-1" onClick={(e) => e.stopPropagation()}>
                  <KebabMenu user={user} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default UsersTab;
